import React, { useEffect, useMemo } from 'react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import RequireDesktopOrIpad from '../components/RequireDesktopOrIpad';
import { useIsMobile } from '../hooks/useIsMobile';
import { PRODUCTION_PATHS, resolveHostedAppUrl } from '../config/siteUrl';
import { auth } from '../services/firebase';

const SEAWOLF_BOOTSTRAP_TOKEN_KEY = 'cognidoor.seawolf.bootstrap.firebase_token';

function getHostedSeaWolfNextPath(baseUrl, currentSuffix = '', search = '') {
  try {
    const url = new URL(baseUrl);
    const basePath = url.pathname && url.pathname !== '/' ? url.pathname.replace(/\/$/, '') : '';
    return `${basePath}${currentSuffix}${search}` || '/sea-wolf';
  } catch {
    return `/seawolf-simulator${currentSuffix}${search}`;
  }
}

/**
 * Sea Wolf Simulator launcher.
 * Redirects to the actual Sea Wolf app.
 * - On localhost: uses http://localhost:8080 (dev proxy target).
 * - Else (Vercel preview, staging, etc.): uses production URL so we never send users to localhost.
 * - Override with REACT_APP_SEAWOLF_APP_URL if needed.
 * - On mobile: show "requires computer or iPad" message instead of redirecting.
 */
function getSeaWolfAppUrl() {
  return resolveHostedAppUrl({
    envName: 'REACT_APP_SEAWOLF_APP_URL',
    localhostUrl: 'http://localhost:8080',
    productionPath: PRODUCTION_PATHS.seawolfSimulator,
  });
}

export default function SeaWolfAppPage() {
  const isMobile = useIsMobile(767);
  const appUrl = useMemo(getSeaWolfAppUrl, []);

  useEffect(() => {
    if (isMobile) return;
    async function handoff() {
      const currentPath = window.location.pathname.replace(/^\/seawolf-app/, '') || '/sea-wolf';
      const nextPath = getHostedSeaWolfNextPath(appUrl, currentPath, window.location.search);
      const target = `${appUrl.replace(/\/$/, '')}/auth/bootstrap?next=${encodeURIComponent(nextPath)}`;

      try {
        const currentUser = auth.currentUser;
        if (currentUser?.getIdToken) {
          const token = await currentUser.getIdToken();
          window.sessionStorage.setItem(SEAWOLF_BOOTSTRAP_TOKEN_KEY, token);
        } else {
          window.sessionStorage.removeItem(SEAWOLF_BOOTSTRAP_TOKEN_KEY);
        }
      } catch {
        window.sessionStorage.removeItem(SEAWOLF_BOOTSTRAP_TOKEN_KEY);
      }

      window.location.assign(target);
    }

    void handoff();
  }, [appUrl, isMobile]);

  if (isMobile) {
    return <RequireDesktopOrIpad backTo="/seawolf-practice" backLabel="View Sea Wolf practice" />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <LoadingSpinner tone="dark" size="md" className="shrink-0" />
        <p className="text-sm font-medium text-white/90">Opening Sea Wolf Simulator…</p>
        <p className="text-xs text-white/60">If you are not redirected, open the app directly.</p>
        <a
          href={`${appUrl.replace(/\/$/, '')}/auth/bootstrap?next=${encodeURIComponent(getHostedSeaWolfNextPath(appUrl, '/sea-wolf'))}`}
          className="text-sm font-semibold text-sky-300 hover:text-sky-200 underline underline-offset-2"
        >
          Open Sea Wolf Simulator
        </a>
      </div>
    </div>
  );
}
